// careerPage/StyledDropdown.tsx

import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface StyledDropdownProps {
    label: string;
    options: string[];
    onSelect?: (value: string) => void;
    width?: string;
    fullPageWidth: number;
}

/**
 * StyledDropdown
 * This is responsive design for tabs in small screen
 */
const StyledDropdown: React.FC<StyledDropdownProps> = ({
    label,
    options,
    onSelect,
    width,
    fullPageWidth,
}) => {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(label);

    const handleSelect = (value: string) => {
        setSelected(value);
        setOpen(false);
        if (onSelect) onSelect(value);
    };

    return (
        <Box
            sx={{
                // 讓整個下拉選單容器以正常區塊流呈現
                position: 'relative',
                width: '100%',
            }}
            data-testid={'styled-dropdown-box'}
        >
            {/* 收合狀態的按鈕 */}
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: '10px',
                    height: '32px',
                    background: '#7A7C81',
                    cursor: 'pointer',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between', // 文字左、箭頭右
                        alignItems: 'center',
                        width: '100%', // 讓內部橫向撐滿
                    }}
                >
                    <Typography
                        sx={{
                            // 移除固定寬度與 margin: '0 auto'
                            fontFamily: 'Poppins',
                            fontWeight: 500,
                            fontSize: '18px',
                            lineHeight: '22px',
                            color: '#FFFFFF',
                            // 文字就會自動靠左
                        }}
                    >
                        {selected}
                    </Typography>
                    <Box sx={{ width: '18px', height: '18px' }}>
                        {open ? (
                            <ExpandLessIcon sx={{ color: 'white' }} />
                        ) : (
                            <ExpandMoreIcon sx={{ color: 'white' }} />
                        )}
                    </Box>
                </Box>
            </Box>

            {/* 展開狀態的選項清單 */}
            {open && (
                <Box
                    sx={{
                        // 若要懸浮在按鈕下方，position: 'absolute' + top: '30px'
                        // 如果想讓它直接往下擠開，就拿掉 position: 'absolute'
                        position: 'absolute',
                        top: '30px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: `${fullPageWidth}px`, // 使用全螢幕寬度
                        maxHeight: '300px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        background: '#FFFFFF',
                        zIndex: 5,
                        boxShadow: '0px 2px 4px rgba(0,0,0,0.15)',
                        borderBottomLeftRadius: '6px', // 左下圓角
                        borderBottomRightRadius: '6px', // 右下圓角
                    }}
                    data-testid={'dropdown-zone'}
                >
                    {options.map((option, index) => (
                        <Box
                            key={index}
                            onClick={() => handleSelect(option)}
                            sx={{
                                position: 'relative', // 為 pseudo-element 提供參考點
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                padding: '8px 0px',
                                gap: '10px',
                                height: '40px',
                                cursor: 'pointer',
                                width: width,
                                margin: 'auto',
                                '&:hover': {
                                    backgroundColor: '#f2f2f2',
                                },
                                // 如果不是最後一個項目，加上 pseudo-element 畫出全螢幕寬的底線
                                ...(index !== options.length - 1 && {
                                    '&:after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: '100vw',
                                        borderBottom: '0.6px solid #7A7C81',
                                    },
                                }),
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    color: '#B75332',
                                }}
                            >
                                {option}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default StyledDropdown;
